import React from 'react';
import { Link } from 'react-router-dom';
import { GuideCallout, GuideH2, GuideH3, GuideList, GuideP } from './GuideProse';
export function TalentVsMarketingAgencies() {
  return <>
      <GuideH2 id="introduction" data-id="element-540" data-mp="32ymuqjzrwxuk">Introduction</GuideH2>
      <GuideP data-id="element-541" data-mp="32y3d74e1cbh2">
        When brands start investing in influencer marketing, one of the first decisions they face is whether to work with
        an agency — and if so, what kind.
      </GuideP>
      <GuideP data-id="element-542" data-mp="2b88mb3aj4dqa">But here’s where things get confusing.</GuideP>
      <GuideP data-id="element-543" data-mp="269dwhhe59owo">
        Most brands don’t actually understand the difference between marketing agencies and talent agencies. And more
        importantly, they don’t understand why that difference matters for campaign performance.
      </GuideP>
      <GuideP data-id="element-544" data-mp="2f7zbx3obknt8">
        This often leads to wasted time, inefficient campaigns, and missed opportunities to work with the right talent.
      </GuideP>

      <GuideH2 id="industry-setup" data-id="element-545" data-mp="3qoz5s3qbfapd">The industry setup</GuideH2>
      <GuideP data-id="element-546" data-mp="3qqtnl96aimp8">In influencer marketing, there are two main types of agencies:</GuideP>
      <GuideList items={['Marketing agencies → represent brands', 'Talent agencies → represent influencers']} data-id="element-547" data-mp="2y4i0o1vsqsa8" />
      <GuideP data-id="element-548" data-mp="3287pmsgdvbvs">
        Some agencies do both. These hybrid agencies represent both brands and talent — meaning they can help with
        strategy while also offering access to their own roster of creators.
      </GuideP>
      <GuideP data-id="element-549" data-mp="2eh2cil1tuxp6">
        But even in those cases, it’s important to understand the underlying roles: marketing agencies are focused on
        helping brands plan and execute campaigns. Talent agencies are focused on managing the influencers on their
        roster and their careers.
      </GuideP>
      <GuideP data-id="element-550" data-mp="2t5ez4rf0z470">And this distinction is where most of the confusion begins.</GuideP>

      <GuideH2 id="what-is-a-roster" data-id="element-551" data-mp="2t7hulqyy5pj4">What is an influencer roster (and why it matters)?</GuideH2>
      <GuideP data-id="element-552" data-mp="1voghg2yqvb5n">
        Before diving into the differences and clearing up that confusion, let's make sure you know what a roster even is
        and how it defines each talent agency.
      </GuideP>
      <GuideP data-id="element-553" data-mp="1smbqv9z6a4p8">Every talent agency is built around one core asset: its roster.</GuideP>
      <GuideP data-id="element-554" data-mp="1ol0alv26uvtp">A roster is simply the group of influencers that an agency represents — often exclusively.</GuideP>
      <GuideP data-id="element-555" data-mp="22gc2luoym86x">Most rosters are highly focused:</GuideP>
      <GuideList items={['Beauty influencers in the US', 'Gaming creators in the UK', 'Lifestyle or travel creators in specific regions']} data-id="element-556" data-mp="1xgpq5ngygvz6" />
      <GuideP data-id="element-557" data-mp="15pjxfs3ke0jx">
        Only a small percentage of talent agencies (roughly ~15%) have large, diverse rosters with 100+ exclusive
        influencers across multiple niches.
      </GuideP>
      <GuideP data-id="element-558" data-mp="1eondsobck1uq">
        This is important because when you work with a talent agency, you’re not just hiring a service — you’re accessing
        a curated group of creators that are vetted.
      </GuideP>
      <GuideCallout data-id="element-559" data-mp="2q30qqy4kawvz">
        If you'd like to learn even more about rosters, we broke this down in detail here:{' '}
        <Link to="/resources/what-is-a-talent-agency-roster" className="font-bold text-zano-pink underline decoration-zano-pink/40 underline-offset-2 hover:decoration-zano-pink" data-id="element-560" data-mp="burgonflzvmk">
          What is an influencer roster (complete guide for brands)
        </Link>
        .
      </GuideCallout>

      <GuideH2 id="strategy-vs-talent" data-id="element-561" data-mp="7vinbjohdzrj">The real difference: strategy vs. talent access</GuideH2>
      <GuideP data-id="element-562" data-mp="vll9xk9mmdir">
        Now, back to the topic at hand. So, what's the difference and why does it matter for campaign performance?
      </GuideP>
      <GuideP data-id="element-563" data-mp="luiaso09c8m4">
        Most brands think agencies are about more hand holding. Like when you need to find a house to buy and prefer to
        go with an agency because you don't know the market so well or don't have the time to invest in finding the right
        one.
      </GuideP>
      <GuideP data-id="element-564" data-mp="1eh325ogqgkpo">
        While that's true also for influencer marketing agencies, there are two different kinds and each serves a
        different purpose to help brands in different ways.
      </GuideP>
      <GuideList items={['Marketing agencies help you figure out what to do and execute.', 'Talent agencies give you access to the people who make it work.']} data-id="element-565" data-mp="3qr358rnvin3n" />
      <GuideP data-id="element-566" data-mp="3hum148dxfphe">
        This matters because in influencer marketing, your results are heavily dependent on your strategy and which
        influencers you choose. It's like two levers that you pull to find the perfect combination for your brand. When
        you get the right set — BOOM, the magic happens. Viral videos, high CPM, brand mentions go up, all the fun stuff.
      </GuideP>
      <GuideP data-id="element-567" data-mp="4k4uxrcuvdk6">
        If you go with a talent agency and no strategy, usually the campaign flops. If you go with a marketing agency but
        completely blind and don't ask for high-quality talent, same. Just because an influencer is being recommended by
        an agency and they match your branding, doesn't mean they are going to deliver good results.
      </GuideP>
      <GuideCallout data-id="element-568" data-mp="zoj9uqru5dtc">Here’s the part most brands overlook: high-quality influencers are usually represented.</GuideCallout>
      <GuideP data-id="element-569" data-mp="14o5p15r8544s">
        If you’re trying to find talent entirely on your own, you’re often missing out on the best creators — because
        they’re already working with talent managers.
      </GuideP>

      <GuideH2 id="high-quality-talent" data-id="element-570" data-mp="1dkn3oiysrk5a">What defines high-quality talent?</GuideH2>
      <GuideP data-id="element-571" data-mp="1ilkhykgoru1i">
        Not all influencers are equal — and choosing the wrong ones is one of the biggest reasons campaigns underperform.
      </GuideP>
      <GuideP data-id="element-572" data-mp="1sb43jxvsa202">High-quality talent typically shares a few key traits:</GuideP>
      <GuideList items={['Consistency → They post regularly and maintain a clear content style', 'Reliability → They deliver on time and don’t remove sponsored content', 'Engaged audience → Their audience interacts (likes, comments), not just views', 'Professionalism → They treat collaborations seriously']} data-id="element-573" data-mp="1xaqpcvpogqkh" />
      <GuideP data-id="element-574" data-mp="672uet909kxk">
        These types of influencers usually don't have the time to handle their inbound and careers, which is why they go
        with talent managers to do it for them. It takes a lot to post consistently, come up with the content, engage
        back with their audience, etc.
      </GuideP>

      <GuideH2 id="when-to-work-with-each" data-id="element-575" data-mp="b6fg3ao54lyk">When should you work with each?</GuideH2>
      <GuideP data-id="element-576" data-mp="pvbo57q75yz6">This is where the decision becomes simple.</GuideP>

      <GuideH3 data-id="element-577" data-mp="kx0d4to61ifd">Work with a marketing agency if:</GuideH3>
      <GuideList items={['You don’t have a clear influencer marketing strategy', 'You need guidance translating your brand into campaigns', 'You want full-service support and campaign management']} data-id="element-578" data-mp="yxumpfmy9pfm" />

      <GuideH3 data-id="element-579" data-mp="30pzduowmkipa">Work with a talent agency if:</GuideH3>
      <GuideList items={['You already know your strategy', 'You understand your target audience', 'You’re looking for the right creators to execute your vision']} data-id="element-580" data-mp="2vqcwc39rkgao" />
      <GuideP data-id="element-581" data-mp="1g7f2d22m49eq">
        If you do go with a marketing agency, do not go in blind. Make sure you review the influencer profiles and act as
        a vetting machine.
      </GuideP>
      <GuideCallout data-id="element-582" data-mp="16h3yffixi07u">
        In other words: if you need direction, go with a marketing agency. If you need talent, go with a talent agency.
      </GuideCallout>

      <GuideH2 id="trade-offs" data-id="element-583" data-mp="sg9outk59t7l">The trade-offs</GuideH2>
      <GuideP data-id="element-584" data-mp="xgxlpvv2149q">Both options have their place — but they come with trade-offs.</GuideP>

      <GuideH3 data-id="element-585" data-mp="jhefx9ppu50l">Marketing agencies</GuideH3>
      <GuideList items={['Higher cost (strategy + execution)', 'Less direct access to talent options', 'More control over your campaign decisions']} data-id="element-586" data-mp="jih77iqya9h1" />

      <GuideH3 data-id="element-587" data-mp="2dvp3leukqt4s">Talent agencies</GuideH3>
      <GuideList items={['Less strategic guidance and post-campaign analysis', 'Require you to be clear on what you want']} data-id="element-588" data-mp="2hw8zkf3evqpc" />
      <GuideP data-id="element-589" data-mp="15p8fzvw4z8tv">That said, the gap is starting to close.</GuideP>
      <GuideP data-id="element-590" data-mp="10pdrfyzu2vbz">
        Many talent agencies today are becoming more sophisticated in how they work with brands. They’re no longer just
        providing access to talent — they’re also offering reporting, insights, and more structured collaboration.
      </GuideP>
      <GuideP data-id="element-591" data-mp="mwefg6z9fj96">Agencies are increasingly using platforms like July, Upfluence, Influencity, and Creator Core to:</GuideP>
      <GuideList items={['Manage campaigns', 'Track performance', 'Share results directly with brands']} data-id="element-592" data-mp="hxbg935yo794" />
      <GuideP data-id="element-593" data-mp="czjdtf7ox2qo">
        This is a major shift. The ability to track live results as a campaign is running — not just after it ends — is
        becoming essential for brands. It gives brands real-time visibility into what’s working, what’s not, and how
        campaigns are performing overall.
      </GuideP>
      <GuideP data-id="element-594" data-mp="22epv11h0e788">
        The same applies to how influencers are selected. Both marketing agencies and talent agencies are starting to
        rely more on tools to:
      </GuideP>
      <GuideList items={['Evaluate creators', 'Compare performance', 'Build shortlists']} data-id="element-595" data-mp="2ev8r42lis3hf" />
      <GuideP data-id="element-596" data-mp="2jm8rjhtul3oo">
        This makes the process much smoother and gives brands far more clarity and confidence in who they’re working
        with.
      </GuideP>
      <GuideP data-id="element-597" data-mp="2onoa938jyy3w">
        At Zano, this is exactly what we’re building toward — giving agencies and brands the ability to not only discover
        talent, but also track campaigns and performance in real time, all in one place.
      </GuideP>

      <GuideH2 id="red-flags" data-id="element-598" data-mp="20vzne2ei31uc">Red flags to watch out for</GuideH2>
      <GuideP data-id="element-599" data-mp="25vca8n5dg8wx">Not all agencies are created equal.</GuideP>
      <GuideP data-id="element-600" data-mp="25w5bx9ph4887">Be cautious if an agency:</GuideP>
      <GuideList items={['Claims to represent influencers they don’t actually manage', 'Overpromises results without understanding your goals', 'Simply sends talent lists without offering structure or reporting']} data-id="element-601" data-mp="3cbeh8xbexdl8" />
      <GuideP data-id="element-602" data-mp="3hl0hfo06wq1g">Strong talent agencies will:</GuideP>
      <GuideList items={['Ask about your goals', 'Suggest relevant creators', 'Provide performance insights or reporting']} data-id="element-603" data-mp="3mlefqebzolma" />

      <GuideH2 id="final-thoughts" data-id="element-604" data-mp="3rdfzelbhlemc">Final thoughts</GuideH2>
      <GuideP data-id="element-605" data-mp="3vf17ltd2t4q7">
        Choosing between a marketing agency and a talent agency isn’t about which one is “better.” It’s about
        understanding what you actually need and how involved you are already with your influencer marketing strategy.
      </GuideP>
      <GuideCallout data-id="element-606" data-mp="33pfxqqcn03sz">
        But if there’s one thing to take away, it’s this: influencer marketing success isn’t just about strategy alone —
        it’s about BOTH strategy AND access to the right talent.
      </GuideCallout>
      <GuideP data-id="element-607" data-mp="mypv8izzvh1r">And in most cases, that talent is already represented.</GuideP>
    </>;
}